// @react
import React, { useRef } from 'react'
// @libraries
import {
  Control,
  Controller,
  FieldErrors,
  UseFormMethods,
  useWatch,
} from 'react-hook-form'
// @commons
import {
  ATD_ca_provinces_options,
  ATD_us_states_options,
} from 'modules/common/forms/fields/_config/addresss'
// @typings
import { CreateAccountFormValues } from 'modules/common/forms/types/createAccount'
// @components
import Grid from '@bit/atd.web.grid'
import InputText from '@bit/atd.web.forms.input-text'
import Select from '@bit/atd.web.select'

type PropsType = {
  control: Control<CreateAccountFormValues>
  formMethods: UseFormMethods<CreateAccountFormValues>
}

type PropsErrorType = {
  errors: FieldErrors
}

const AddressFields = ({ control, errors }: PropsType & PropsErrorType) => {
  /******************************************
   * HOOKS
   ******************************************/

  const watchCountry = useWatch<string>({
    control,
    name: `countryCode`,
  })

  /******************************************
   * REFS
   ******************************************/

  const addressRef = useRef<HTMLInputElement | null>(null)
  const zipCodeRef = useRef<HTMLInputElement | null>(null)
  const cityRef = useRef<HTMLInputElement | null>(null)
  const stateRef = useRef<HTMLInputElement | null>(null)

  /******************************************
   * RENDER
   ******************************************/

  let addressLabel = 'Address'
  let address2Label = 'Address 2'
  let address2LabelWithPeriod = 'Address 2.'
  let address3Label = 'Address 3'
  let postalCodeLabel = 'Zip Code'
  let stateLabel = 'State'
  let stateOptions: null | { value: string; label: string }[] = null
  if (watchCountry) {
    switch (watchCountry) {
      case 'US':
        addressLabel = 'Address'
        address2Label = 'Apt, Suite, Etc.'
        address2LabelWithPeriod = 'Apt, Suite, Etc.'
        postalCodeLabel = 'Zip Code'
        stateLabel = 'State'
        stateOptions = ATD_us_states_options
        break
      case 'CA':
        addressLabel = 'Street'
        postalCodeLabel = 'Postal Code'
        stateLabel = 'Province'
        stateOptions = ATD_ca_provinces_options
        break
      case 'CN':
        addressLabel = 'Bldg # & Street'
        address3Label = 'District'
        stateLabel = 'Province'
        break
      default:
        addressLabel = 'Address 1 / GPO Box'
        postalCodeLabel = 'Post Code'
    }
  }

  return (
    <>
      {/* Address 1 */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          onFocus={() => addressRef?.current?.focus()}
          name="address1"
          render={({ onChange, onBlur, value }) => (
            <InputText
              name="address1"
              label={addressLabel}
              placeholder={`Enter your ${addressLabel.toLowerCase()}.`}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              inputRef={addressRef}
              required
              error={!!errors?.address1}
              errorText={errors?.address1?.message}
            />
          )}
        />
      </Grid>

      {/* Address 2 */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="address2"
          render={({ onChange, onBlur, value }) => (
            <InputText
              name="address2"
              label={address2Label}
              placeholder={`Enter your ${address2LabelWithPeriod.toLowerCase()}`}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
            />
          )}
        />
      </Grid>

      {/* Address 3 */}
      {watchCountry === 'cn' && (
        <Grid xs={12} sm={6} item>
          <Controller
            control={control}
            name="address3"
            render={({ onChange, onBlur, value }) => (
              <InputText
                name="address3"
                label={address3Label}
                placeholder={`Enter your ${address3Label.toLowerCase()}.`}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
              />
            )}
          />
        </Grid>
      )}

      {/* City */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="city"
          onFocus={() => cityRef?.current?.focus()}
          render={({ onChange, onBlur, value }) => (
            <InputText
              required
              name="city"
              label="City"
              placeholder="Enter your city."
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              inputRef={cityRef}
              error={!!errors?.city}
              errorText={errors?.city?.message}
            />
          )}
        />
      </Grid>

      {/* State */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="state"
          onFocus={() => stateRef?.current?.focus()}
          render={({ onChange, onBlur, value }) => (
            <>
              {stateOptions !== null ? (
                <Select
                  native
                  required={watchCountry === 'CA' || watchCountry === 'US'}
                  name="state"
                  label={stateLabel}
                  placeholder={`Choose your ${stateLabel.toLowerCase()}.`}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  inputRef={stateRef}
                  options={stateOptions}
                  error={!!errors?.state}
                  errorText={errors?.state?.message}
                />
              ) : (
                <InputText
                  required={watchCountry === 'CA' || watchCountry === 'US'}
                  name="state"
                  label={stateLabel}
                  placeholder={`Enter your ${stateLabel.toLowerCase()}.`}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  inputRef={stateRef}
                  error={!!errors?.state}
                  errorText={errors?.state?.message}
                />
              )}
            </>
          )}
        />
      </Grid>

      {/* Postal Code */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="postalCode"
          onFocus={() => zipCodeRef?.current?.focus()}
          render={({ onChange, onBlur, value }) => (
            <InputText
              required
              name="postalCode"
              label={postalCodeLabel}
              placeholder={`Enter your ${postalCodeLabel.toLowerCase()}.`}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              inputRef={zipCodeRef}
              error={!!errors?.postalCode}
              errorText={errors?.postalCode?.message}
            />
          )}
        />
      </Grid>
    </>
  )
}

export default AddressFields
